import { RegionContext } from "@certn/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useContext } from "react";
import { Route, Routes } from "react-router-dom";

import { SettingsLayout } from "./SettingsLayout";
import {
  userPagePermissions,
  checkArgumentsTemplatePermissions,
  useCanAccessSettings,
  useCanViewBranding,
  useCanViewGroups,
} from "../../components/permissions/Permissions";
import { usePermissions } from "../../components/permissions/PermissionsProvider";
import { useUser } from "../../hooks/useUser";
import { EmploymentVerificationTemplateListView } from "../../pages/employment-verification-templates/EmploymentVerificationTemplateListView";
import { VerificationTemplatesPage } from "../../pages/employment-verification-templates/create/VerificationTemplatesPage";
import { VerificationTemplate } from "../../pages/employment-verification-templates/edit/VerificationTemplate";
import { CredentialVerificationTemplateListView } from "../../pages/credential-verification-templates/CredentialVerificationTemplateListView";
import { CredentialTemplatesPage } from "../../pages/credential-verification-templates/create/CredentialTemplatesPage";
import { CredentialTemplate } from "../../pages/credential-verification-templates/edit/CredentialTemplate";
import { BankruptcyTemplateListView } from "../../pages/bankruptcy-templates/BankruptcyTemplateListView";
import { BankruptcyTemplateCreatePage } from "../../pages/bankruptcy-templates/BankruptcyTemplateCreatePage";
import { BankruptcyTemplateDetailPage } from "../../pages/bankruptcy-templates/BankruptcyTemplateDetailPage";
import { CriminalRecordTemplateListView } from "../../pages/criminal-record-templates/CriminalRecordTemplateListView";
import { CriminalRecordTemplatesPage } from "../../pages/criminal-record-templates/create/CriminalRecordTemplatesPage";
import { CriminalRecordTemplate } from "../../pages/criminal-record-templates/edit/CriminalRecordTemplate";
import { CivilLitigationTemplateListView } from "../../pages/civil-litigation-templates/CivilLitigationTemplateListView";
import { CivilLitigationTemplateCreatePage } from "../../pages/civil-litigation-templates/CivilLitigationTemplateCreatePage";
import { CivilLitigationTemplateDetailPage } from "../../pages/civil-litigation-templates/CivilLitigationTemplateDetailPage";
import { DirectorshipTemplateListView } from "../../pages/directorship-templates/DirectorshipTemplateListView";
import { DirectorshipTemplateCreatePage } from "../../pages/directorship-templates/DirectorshipTemplateCreatePage";
import { DirectorshipTemplateDetailPage } from "../../pages/directorship-templates/DirectorshipTemplateDetailPage";
import { CreditReportTemplateListView } from "../../pages/credit-report-templates/CreditReportTemplateListView";
import { CreditReportTemplateCreatePage } from "../../pages/credit-report-templates/CreditReportTemplateCreatePage";
import { CreditReportTemplateDetailPage } from "../../pages/credit-report-templates/CreditReportTemplateDetailPage";
import { InternationalCriminalDatabaseTemplateListView } from "../../pages/international-criminal-database-templates/InternationalCriminalDatabaseTemplateListView";
import { InternationalCriminalDatabaseTemplateCreatePage } from "../../pages/international-criminal-database-templates/InternationalCriminalDatabaseTemplateCreatePage";
import { InternationalCriminalDatabaseTemplateDetailPage } from "../../pages/international-criminal-database-templates/InternationalCriminalDatabaseTemplateDetailPage";
import { Integrations } from "../../pages/integrations/Integrations";
import { AtsIntegrationsRoot } from "../../pages/ats-integrations/AtsIntegrationsRoot";
import { CheckSettings } from "../../pages/settings/CheckSettings";
import { Template } from "../../pages/settings/new-questionnaire-template/Template";
import { TagsListView } from "../../pages/tags/TagsListView";
import { BrandingSettings } from "../../pages/settings/branding";
import { CreateUser } from "../../pages/users/CreateUser";
import { UserDetails } from "../../pages/users/UserDetails";
import { UserListView } from "../../pages/users/UserListView";
import { SecuritySettingsPage } from "../../pages/settings/security/SecuritySettingsPage";
import { AtsIntegrationSetupRoutes } from "../../pages/ats-integrations/AtsIntegrationSetupRoutes";
import { AtsIntegrationEditRoutes } from "../../pages/ats-integrations/AtsIntegrationEditRoutes";
import { TemplatesTable } from "../../pages/templates/TemplatesTable";
import { useTranslation } from "react-i18next";
import { ExperienceVerificationTemplatesTable } from "../../pages/experience-verification-templates/ExperienceVerificationTemplatesTable";
import { ExperienceVerificationTemplateCreationPage } from "../../pages/experience-verification-templates/ExperienceVerificationTemplateCreationPage";
import { ExperienceVerificationTemplateUpdatePage } from "../../pages/experience-verification-templates/ExperienceVerificationTemplateUpdatePage";
import { ReferenceCheckTemplateListView } from "../../pages/reference-check-templates/ReferenceCheckTemplateListView";
import { ReferenceCheckTemplateCreatePage } from "../../pages/reference-check-templates/ReferenceCheckTemplateCreatePage";
import { ReferenceCheckTemplateDetailPage } from "../../pages/reference-check-templates/ReferenceCheckTemplateDetailPage";
import { FitnessProprietyTemplateListView } from "../../pages/fitness-propriety-templates/FitnessProprietyTemplateListView";
import { FitnessProprietyTemplateView } from "../../pages/fitness-propriety-templates/FitnessProprietyTemplateView";
import { CustomTemplateCreationPage } from "../../pages/templates/CustomTemplateCreationPage";
import { CustomTemplateEditPage } from "../../pages/templates/CustomTemplateEditPage";
import {
  EducationVerificationTemplateDetailPage,
  EducationVerificationTemplateListView,
} from "../../pages/education-verification-templates/EducationVerificationTemplates";

export function SettingsRoutes() {
  const {
    clientPortalOrderNonBiometricIdCanada,
    clientPortalQuestionnaireTemplates,
    employmentVerificationsOrderable,
    credentialVerificationsOrderable,
    clientPortalUserManagement,
    enableAddressBasedInternationalCriminalRecordCheckOrdering,
    enableAddressBasedBankruptcyCheckOrdering,
    enableAddressBasedDirectorshipCheckOrdering,
    enableAddressBasedCreditCheckOrdering,
    enableCivilLitigationRecordCheck,
    enableInternationalCriminalDatabase,
    releaseCustomQuestionnaireCheck,
    releaseExperienceVerifications,
    releaseCustomQuestionnaireTemplateBuilder,
    enableCobrandingForApplications,
    mfaEnabled,
    enableDataRetentionSettings,
  } = useFlags();

  const region = useContext(RegionContext);
  const showDataRetention = enableDataRetentionSettings && region !== "au";
  const { hasPermission } = usePermissions();
  const { details } = useUser();
  const { t } = useTranslation();
  const canAccessSettings = useCanAccessSettings();
  const canViewBranding = useCanViewBranding();
  const canViewGroups = useCanViewGroups();
  const checkSettingsVisible =
    clientPortalOrderNonBiometricIdCanada &&
    hasPermission({
      action: "read",
      target: "check_settings",
      scope: "all",
    });

  const canViewCustomQuestionnaireTemplates =
    releaseCustomQuestionnaireCheck &&
    hasPermission(checkArgumentsTemplatePermissions);

  const canViewExperienceVerificationTemplates =
    releaseExperienceVerifications &&
    hasPermission(checkArgumentsTemplatePermissions);

  const hasTenacyPermissablePurpose =
    details?.customer_groups?.some(
      (group) => group.permissible_purpose === "TENANCY"
    ) || false;
  const hasInvestingOrLendingPermissiblePurpose =
    details?.customer_groups?.some(
      (group) =>
        group.permissible_purpose === "INVESTING" ||
        group.permissible_purpose === "LENDING"
    ) || false;

  const canViewTenancyQuestionnaireTemplates =
    !!clientPortalQuestionnaireTemplates &&
    hasTenacyPermissablePurpose &&
    hasPermission({
      action: "read",
      target: "questionnaire",
      scope: "all",
    });

  const canViewFitnessProprietyTemplates =
    !!clientPortalQuestionnaireTemplates &&
    (hasTenacyPermissablePurpose || hasInvestingOrLendingPermissiblePurpose);

  return (
    <Routes>
      {canViewTenancyQuestionnaireTemplates && (
        <>
          <Route
            path="/questionnaire-templates"
            element={
              <SettingsLayout>
                <TemplatesTable
                  templateType="TENANCY"
                  title={t("Questionnaire")}
                  description={t(`A complete list of all questionnaire templates that have been
              created for your organization. Questionnaire templates allow you
              to create customizable sets of background checks tailored to the
              unique needs of your organization or department.`)}
                />
              </SettingsLayout>
            }
          />
          <Route path="/questionnaire-templates/:id/*" element={<Template />} />
          <Route
            path="/questionnaire-templates/create"
            element={<Template />}
          />
        </>
      )}
      {canViewFitnessProprietyTemplates && (
        <Route
          path="/fitness-propriety-templates"
          element={
            <SettingsLayout>
              <FitnessProprietyTemplateListView />
            </SettingsLayout>
          }
        />
      )}
      {canViewFitnessProprietyTemplates && (
        <Route
          path="/fitness-propriety-templates/:id/*"
          element={<FitnessProprietyTemplateView />}
        />
      )}
      {hasPermission({
        action: "read",
        target: "tag",
        scope: "all",
      }) && (
        <Route
          path="/tags"
          element={
            <SettingsLayout>
              <TagsListView />
            </SettingsLayout>
          }
        />
      )}
      {enableCobrandingForApplications && canViewBranding && (
        <Route
          path="/branding"
          element={
            <SettingsLayout>
              <BrandingSettings />
            </SettingsLayout>
          }
        />
      )}
      {canViewGroups &&
        clientPortalUserManagement &&
        hasPermission(userPagePermissions) && (
          <>
            <Route
              path="/users"
              element={
                <SettingsLayout>
                  <UserListView />
                </SettingsLayout>
              }
            />
            <Route path="/user/:userId/*" element={<UserDetails />} />
            <Route path="/user/create" element={<CreateUser />} />
          </>
        )}
      <Route
        path="/employment-verification-templates/create"
        element={<VerificationTemplatesPage />}
      />
      <Route
        path="/employment-verification-templates/:templateId/*"
        element={<VerificationTemplate />}
      />
      {hasPermission({
        action: "configure",
        target: "public_api",
        scope: "all",
      }) && (
        <>
          <Route
            path="/integrations/*"
            element={
              <SettingsLayout>
                <Integrations />
              </SettingsLayout>
            }
          />
          <Route
            path="/ats-integrations"
            element={
              <SettingsLayout>
                <AtsIntegrationsRoot />
              </SettingsLayout>
            }
          />
          <Route
            path="/integrations/setup/*"
            element={<AtsIntegrationSetupRoutes />}
          />
          <Route
            path="/integrations/edit/*"
            element={<AtsIntegrationEditRoutes />}
          />
        </>
      )}
      {(mfaEnabled || showDataRetention) && canAccessSettings && (
        <Route path="/security" element={<SecuritySettingsPage />} />
      )}
      {employmentVerificationsOrderable &&
        hasPermission(checkArgumentsTemplatePermissions) && (
          <Route
            path="/employment-verification-templates"
            element={
              <SettingsLayout>
                <EmploymentVerificationTemplateListView />
              </SettingsLayout>
            }
          />
        )}
      {hasPermission(checkArgumentsTemplatePermissions) && (
        <>
          <Route
            path="/education-verification-templates"
            element={
              <SettingsLayout>
                <EducationVerificationTemplateListView />
              </SettingsLayout>
            }
          />
          <Route
            path="/education-verification-templates/:templateId"
            element={<EducationVerificationTemplateDetailPage />}
          />
        </>
      )}
      {credentialVerificationsOrderable &&
        hasPermission(checkArgumentsTemplatePermissions) && (
          <>
            <Route
              path="/credential-verification-templates"
              element={
                <SettingsLayout>
                  <CredentialVerificationTemplateListView />
                </SettingsLayout>
              }
            />
            <Route
              path="/credential-verification-templates/create"
              element={<CredentialTemplatesPage />}
            />
            <Route
              path="/credential-verification-templates/:templateId/*"
              element={<CredentialTemplate />}
            />
          </>
        )}
      {enableAddressBasedInternationalCriminalRecordCheckOrdering &&
        hasPermission(checkArgumentsTemplatePermissions) && (
          <>
            <Route
              path="/criminal-record-templates"
              element={
                <SettingsLayout>
                  <CriminalRecordTemplateListView />
                </SettingsLayout>
              }
            />
            <Route
              path="/criminal-record-templates/create"
              element={<CriminalRecordTemplatesPage />}
            />
            <Route
              path="/criminal-record-templates/:templateId/*"
              element={<CriminalRecordTemplate />}
            />
          </>
        )}
      {enableAddressBasedBankruptcyCheckOrdering &&
        hasPermission(checkArgumentsTemplatePermissions) && (
          <>
            <Route
              path="/bankruptcy-templates"
              element={
                <SettingsLayout>
                  <BankruptcyTemplateListView />
                </SettingsLayout>
              }
            />
            <Route
              path="/bankruptcy-templates/create"
              element={<BankruptcyTemplateCreatePage />}
            />
            <Route
              path="/bankruptcy-templates/:templateId/*"
              element={<BankruptcyTemplateDetailPage />}
            />
          </>
        )}
      {enableInternationalCriminalDatabase &&
        hasPermission(checkArgumentsTemplatePermissions) && (
          <>
            <Route
              path="/international-criminal-database-templates"
              element={
                <SettingsLayout>
                  <InternationalCriminalDatabaseTemplateListView />
                </SettingsLayout>
              }
            />
            <Route
              path="/international-criminal-database-templates/create"
              element={<InternationalCriminalDatabaseTemplateCreatePage />}
            />
            <Route
              path="/international-criminal-database-templates/:templateId/*"
              element={<InternationalCriminalDatabaseTemplateDetailPage />}
            />
          </>
        )}
      {enableCivilLitigationRecordCheck &&
        hasPermission(checkArgumentsTemplatePermissions) && (
          <>
            <Route
              path="/civil-litigation-templates"
              element={
                <SettingsLayout>
                  <CivilLitigationTemplateListView />
                </SettingsLayout>
              }
            />
            <Route
              path="/civil-litigation-templates/create"
              element={<CivilLitigationTemplateCreatePage />}
            />
            <Route
              path="/civil-litigation-templates/:templateId/*"
              element={<CivilLitigationTemplateDetailPage />}
            />
          </>
        )}
      {enableAddressBasedDirectorshipCheckOrdering &&
        hasPermission(checkArgumentsTemplatePermissions) && (
          <>
            <Route
              path="/directorship-templates"
              element={
                <SettingsLayout>
                  <DirectorshipTemplateListView />
                </SettingsLayout>
              }
            />
            <Route
              path="/directorship-templates/create"
              element={<DirectorshipTemplateCreatePage />}
            />
            <Route
              path="/directorship-templates/:templateId/*"
              element={<DirectorshipTemplateDetailPage />}
            />
          </>
        )}
      {enableAddressBasedCreditCheckOrdering &&
        hasPermission(checkArgumentsTemplatePermissions) && (
          <>
            <Route
              path="/credit-report-templates"
              element={
                <SettingsLayout>
                  <CreditReportTemplateListView />
                </SettingsLayout>
              }
            />
            <Route
              path="/credit-report-templates/create"
              element={<CreditReportTemplateCreatePage />}
            />
            <Route
              path="/credit-report-templates/:templateId/*"
              element={<CreditReportTemplateDetailPage />}
            />
          </>
        )}
      {checkSettingsVisible && (
        <Route
          path="/check-settings"
          element={
            <SettingsLayout>
              <CheckSettings />
            </SettingsLayout>
          }
        />
      )}

      {canViewCustomQuestionnaireTemplates && (
        <>
          <Route
            path="/custom-questionnaire-templates"
            element={
              <SettingsLayout>
                <TemplatesTable
                  templateType="CUSTOM"
                  title={t("Custom questionnaire")}
                />
              </SettingsLayout>
            }
          />
          <Route
            path="/custom-questionnaire-templates/:id/*"
            element={<CustomTemplateEditPage />}
          />
          {releaseCustomQuestionnaireTemplateBuilder && (
            <Route
              path="/custom-questionnaire-templates/create"
              element={<CustomTemplateCreationPage />}
            />
          )}
        </>
      )}
      {canViewExperienceVerificationTemplates && (
        <>
          <Route
            path="/experience-verification-templates"
            element={
              <SettingsLayout>
                <ExperienceVerificationTemplatesTable />
              </SettingsLayout>
            }
          />
          <Route
            path="/experience-verification-templates/create"
            element={<ExperienceVerificationTemplateCreationPage />}
          />
          <Route
            path="/experience-verification-templates/:templateId/*"
            element={<ExperienceVerificationTemplateUpdatePage />}
          />
        </>
      )}
      {/* TODO: gate behind caseManagementReferenceSuperunitFirstFulfillment once flag is client-side enabled */}
      {hasPermission(checkArgumentsTemplatePermissions) && (
        <>
          <Route
            path="/reference-check-templates"
            element={
              <SettingsLayout>
                <ReferenceCheckTemplateListView />
              </SettingsLayout>
            }
          />
          <Route
            path="/reference-check-templates/create"
            element={<ReferenceCheckTemplateCreatePage />}
          />
          <Route
            path="/reference-check-templates/:templateId/*"
            element={<ReferenceCheckTemplateDetailPage />}
          />
        </>
      )}
      <Route path="*" element={<SettingsLayout />} />
    </Routes>
  );
}
